import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { formatRupiah, MONTHS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MonthYearPicker } from "@/components/inputs";

function Row({ label, value, negative, strong }) {
  return (
    <div className={`flex items-center justify-between py-3 border-b border-border last:border-0 ${strong ? "font-semibold" : ""}`}>
      <span className="text-sm">{label}</span>
      <span className={`tabular-nums ${negative ? "text-destructive" : ""} ${strong ? "text-base" : "text-sm"}`}>
        {negative ? "- " : ""}{formatRupiah(value)}
      </span>
    </div>
  );
}

export default function EmployeeDashboard() {
  const now = new Date();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState(`${now.getMonth() + 1}-${now.getFullYear()}`);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/payroll/me/history").then((res) => setHistory(res.data));
  }, []);

  const [selMonth, selYear] = period.split("-").map(Number);
  useEffect(() => {
    api.get(`/payroll/me?month=${selMonth}&year=${selYear}`).then((res) => setCurrent(res.data));
  }, [selMonth, selYear]);
  const p = current;

  // build period options: current + all history months (unique)
  const optionSet = new Map();
  optionSet.set(`${now.getMonth() + 1}-${now.getFullYear()}`, { m: now.getMonth() + 1, y: now.getFullYear() });
  history.forEach((h) => optionSet.set(`${h.month}-${h.year}`, { m: h.month, y: h.year }));
  const options = Array.from(optionSet.entries());

  const downloadPayslip = async (m, y) => {
    setDownloading(true);
    try {
      const res = await api.get(`/payroll/me/payslip?month=${m}&year=${y}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Gagal mengunduh slip gaji");
    } finally {
      setDownloading(false);
    }
  };

  const sknStats = [
    { label: "Gaji Pokok Terdaftar", value: p?.base_salary, icon: "account_balance", tone: "text-[#00364e]", bg: "bg-[#eff4ff]" },
    { label: "Tunjangan & Insentif", value: p?.variable_total, icon: "add_circle", tone: "text-[#059669]", bg: "bg-emerald-50", prefix: "+ " },
    { label: "Total Potongan & Pajak", value: (p?.pph21_amount || 0) + (p?.bpjs_amount || 0) + (p?.bpjs_kes_amount || 0) + (p?.deduction_components || []).reduce((s, v) => s + Number(v.amount || 0), 0), icon: "remove_circle", tone: "text-[#DC2626]", bg: "bg-red-50", prefix: "− " },
  ];

  return (
    <AppLayout title="Gaji Saya">
      <div className="max-w-6xl space-y-6 animate-fade-up">
        <div>
          <p className="text-[14px] text-[#64748B]">Panel Karyawan <span className="mx-1">›</span> <span className="text-[#006398] font-medium">Gaji Saya</span></p>
          <h1 className="skn-h1 text-[28px] md:text-[36px] leading-tight mt-1">Gaji &amp; Kompensasi Saya</h1>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-[#00364e] text-white p-6 md:p-8 shadow-md">
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-1 max-w-xl">
              <span className="inline-block px-2.5 py-1 rounded-full bg-white/15 text-[11px] font-semibold tracking-[0.2em] uppercase">Periode Penggajian: {MONTHS[selMonth - 1]} {selYear}</span>
              <p className="text-[14px] text-[#9fccec] pt-1">Total Pendapatan Bersih (Take Home Pay)</p>
              <p className="text-[36px] md:text-[44px] font-bold tracking-tight tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }} data-testid="net-salary-value">
                {p ? formatRupiah(p.net_salary) : "…"}
              </p>
              <p className="text-[14px] text-[#9fccec]/80">{MONTHS[selMonth - 1]} {selYear} · setelah PPH 21 &amp; BPJS</p>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <MonthYearPicker
                month={selMonth}
                year={selYear}
                onChange={(m, y) => setPeriod(`${m}-${y}`)}
                years={Array.from(new Set([now.getFullYear(), ...history.map((h) => h.year)])).sort((a, b) => b - a)}
                monthTestId="payroll-period-month"
                yearTestId="payroll-period-year"
                className="w-64"
              />
              <Button className="gap-2 bg-white text-[#00364e] hover:bg-[#eff4ff]" onClick={() => downloadPayslip(selMonth, selYear)} disabled={downloading} data-testid="download-payslip-button">
                <span className="material-symbols-outlined text-[20px]">download</span> {downloading ? "Menyiapkan…" : "Unduh Slip Gaji (PDF)"}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sknStats.map((s, i) => (
            <div key={s.label} className="rounded-xl bg-white p-4 shadow-sm animate-stagger" style={{ "--stagger-delay": `${i * 70}ms` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="skn-section-label">{s.label}</span>
                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center ${s.tone}`}>
                  <span className="material-symbols-outlined text-[20px]">{s.icon}</span>
                </div>
              </div>
              <div className={`text-[24px] font-bold tabular-nums ${s.tone === "text-[#00364e]" ? "text-[#0b1c30]" : s.tone}`} style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                {p ? `${s.prefix || ""}${formatRupiah(s.value)}` : "…"}
              </div>
            </div>
          ))}
        </div>

        <Card className="p-6 bg-white border-[#E2E8F0]/80 shadow-sm">
          <h3 className="text-[20px] font-bold text-[#0b1c30] mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Rincian Slip Gaji</h3>
          <p className="text-[14px] text-[#64748B] mb-4">Pendapatan dan potongan periode berjalan</p>
          {p ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl bg-[#eff4ff] p-4">
                <p className="skn-section-label !text-[#059669] mb-2">A. Pendapatan</p>
                <Row label="Gaji Pokok" value={p.base_salary} />
                {p.variable_components?.map((v, i) => (
                  <Row key={i} label={v.name} value={v.amount} muted />
                ))}
                <Row label="Total Bruto" value={p.gross_salary} strong />
              </div>
              <div className="rounded-xl bg-[#eff4ff] p-4">
                <p className="skn-section-label !text-[#DC2626] mb-2">B. Potongan</p>
                <Row label={`PPH 21 (${p.pph21_percent}%)`} value={p.pph21_amount} negative />
                <Row label={`BPJS TK (${p.bpjs_percent}%)`} value={p.bpjs_amount} negative />
                <Row label={`BPJS Kesehatan (${p.bpjs_kes_percent || 0}%)`} value={p.bpjs_kes_amount || 0} negative />
                {p.deduction_components?.map((v, i) => (
                  <Row key={i} label={v.name} value={v.amount} negative muted />
                ))}
                <Row label="Total Potongan" value={(p.pph21_amount || 0) + (p.bpjs_amount || 0) + (p.bpjs_kes_amount || 0) + (p.deduction_components || []).reduce((s, v) => s + Number(v.amount || 0), 0)} negative strong />
              </div>
            </div>
          ) : (
            <p className="text-[#64748B] text-sm">Memuat…</p>
          )}
          {p && (
            <div className="mt-4 p-4 rounded-xl bg-[#eff4ff] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="font-bold text-[#0b1c30]">Gaji Bersih Diterima (Take Home Pay)</span>
              <span className="text-[20px] font-bold tabular-nums text-[#00364e]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{formatRupiah(p.net_salary)}</span>
            </div>
          )}
        </Card>

        <Card className="p-6 bg-white border-[#E2E8F0]/80 shadow-sm">
          <h3 className="text-[20px] font-bold text-[#0b1c30] mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Riwayat Arsip Slip Gaji</h3>
          <p className="text-[14px] text-[#64748B] mb-4">Riwayat pembayaran dan unduhan slip</p>
          {history.length === 0 ? (
            <p className="text-[#64748B] text-sm">Belum ada riwayat gaji.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={`${h.month}-${h.year}`} className="flex items-center justify-between rounded-lg border border-[#E2E8F0]/80 px-4 py-3" data-testid={`history-row-${i}`}>
                  <div>
                    <p className="font-semibold text-[#0b1c30]">{MONTHS[h.month - 1]} {h.year}</p>
                    <p className="text-xs text-[#64748B]">Bruto {formatRupiah(h.gross_salary)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold tabular-nums text-[#00364e]">{formatRupiah(h.net_salary)}</span>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => downloadPayslip(h.month, h.year)} data-testid={`history-download-${i}`}>
                      <span className="material-symbols-outlined text-[16px]">download</span> PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
